/**
 * Permission Registry and Utilities
 * 
 * This file defines all available permissions and provides utilities
 * for checking permissions throughout the application.
 */

// Define all available permissions with their metadata
export const PERMISSIONS = {
  // Booking Management
  VIEW_BOOKINGS: {
    key: 'view_bookings',
    label: 'View Bookings',
    description: 'View all bookings and appointments',
    category: 'Bookings',
    requiredFor: ['/supplier/bookings', '/supplier/bookings/calendar']
  },
  MANAGE_BOOKINGS: {
    key: 'manage_bookings',
    label: 'Manage Bookings',
    description: 'Create, edit, and cancel bookings',
    category: 'Bookings',
    requiredFor: ['/supplier/bookings/new', '/supplier/bookings/:id']
  },

  // Availability Management
  VIEW_AVAILABILITY: {
    key: 'view_availability',
    label: 'View Availability',
    description: 'View team member availability and schedules',
    category: 'Availability',
    requiredFor: ['/supplier/bookings/calendar']
  },
  MANAGE_AVAILABILITY: {
    key: 'manage_availability',
    label: 'Manage Availability',
    description: 'Update team member schedules and availability',
    category: 'Availability',
    requiredFor: ['/supplier/bookings/services']
  },

  // Profile Management
  VIEW_PROFILE: {
    key: 'view_profile',
    label: 'View Profile',
    description: 'View business profile and details',
    category: 'Profile',
    requiredFor: ['/supplier/profile']
  },
  EDIT_PROFILE: {
    key: 'edit_profile',
    label: 'Edit Profile',
    description: 'Edit business profile and settings',
    category: 'Profile',
    requiredFor: ['/supplier/edit-profile']
  },

  // Staff Management
  MANAGE_STAFF: {
    key: 'manage_staff',
    label: 'Manage Staff',
    description: 'Invite, edit, and manage team members',
    category: 'Staff',
    requiredFor: ['/supplier/team-members']
  },

  // Analytics
  VIEW_ANALYTICS: {
    key: 'view_analytics',
    label: 'View Analytics',
    description: 'View business analytics and reports',
    category: 'Analytics',
    requiredFor: ['/supplier/analytics']
  },

  // Policies
  MANAGE_POLICIES: {
    key: 'manage_policies',
    label: 'Manage Policies',
    description: 'Manage business policies and terms',
    category: 'Policies',
    requiredFor: ['/supplier/policies']
  },

  // Messaging
  VIEW_MESSAGES: {
    key: 'view_messages',
    label: 'View Messages',
    description: 'View customer messages and conversations',
    category: 'Messages',
    requiredFor: ['/supplier/messages']
  },
  SEND_MESSAGES: {
    key: 'send_messages',
    label: 'Send Messages',
    description: 'Send messages to customers',
    category: 'Messages',
    requiredFor: ['/supplier/messages']
  },

  // Portfolio
  MANAGE_PORTFOLIO: {
    key: 'manage_portfolio',
    label: 'Manage Portfolio',
    description: 'Manage business portfolio and gallery',
    category: 'Portfolio',
    requiredFor: ['/supplier/portfolio']
  },

  // Financials
  VIEW_FINANCIALS: {
    key: 'view_financials',
    label: 'View Financials',
    description: 'View financial reports and billing',
    category: 'Financials',
    requiredFor: ['/supplier/billing']
  },
  MANAGE_FINANCIALS: {
    key: 'manage_financials',
    label: 'Manage Financials',
    description: 'Manage billing and financial settings',
    category: 'Financials',
    requiredFor: ['/supplier/billing']
  },

  // Services
  MANAGE_SERVICES: {
    key: 'manage_services',
    label: 'Manage Services',
    description: 'Create and manage service types',
    category: 'Services',
    requiredFor: ['/supplier/bookings/services']
  }
};

// Group permissions by category for easier management
export const PERMISSIONS_BY_CATEGORY = Object.values(PERMISSIONS).reduce((acc, permission) => {
  if (!acc[permission.category]) {
    acc[permission.category] = [];
  }
  acc[permission.category].push(permission);
  return acc;
}, {});

// Default permissions for each role
export const DEFAULT_ROLE_PERMISSIONS = {
  owner: Object.values(PERMISSIONS).map((permission) => permission.key), // Owners have all permissions
  admin: Object.values(PERMISSIONS).map((permission) => permission.key), // Admins default to full access
  manager: [
    'view_bookings',
    'manage_bookings',
    'view_availability',
    'manage_availability',
    'view_profile',
    'edit_profile',
    'view_analytics',
    'view_messages',
    'send_messages',
    'manage_portfolio',
    'manage_services'
  ],
  staff: [
    'view_bookings',
    'view_profile',
    'view_messages',
    'send_messages'
  ]
};

/**
 * Check if a user has a specific permission
 * @param {Array} userPermissions - Array of permission keys the user has
 * @param {string} permissionKey - The permission key to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permissionKey) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return userPermissions.includes(permissionKey);
};

/**
 * Check if a user has any of the specified permissions
 * @param {Array} userPermissions - Array of permission keys the user has
 * @param {Array} permissionKeys - Array of permission keys to check
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, permissionKeys) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return permissionKeys.some(key => userPermissions.includes(key));
};

/**
 * Check if a user has all of the specified permissions
 * @param {Array} userPermissions - Array of permission keys the user has
 * @param {Array} permissionKeys - Array of permission keys to check
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, permissionKeys) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }
  return permissionKeys.every(key => userPermissions.includes(key));
};

/**
 * Get permissions required for a specific route
 * @param {string} route - The route path
 * @returns {Array} Array of permission keys required for the route
 */
export const getPermissionsForRoute = (route) => {
  const requiredPermissions = [];
  
  Object.values(PERMISSIONS).forEach(permission => {
    if (permission.requiredFor) {
      permission.requiredFor.forEach(requiredRoute => {
        // Convert route pattern to regex for matching
        const pattern = requiredRoute
          .replace(/:\w+/g, '[^/]+') // Replace :param with [^/]+
          .replace(/\*/g, '.*'); // Replace * with .*
        const regex = new RegExp(`^${pattern}$`);
        
        if (regex.test(route)) {
          requiredPermissions.push(permission.key);
        }
      });
    }
  });
  
  return [...new Set(requiredPermissions)]; // Remove duplicates
};

/**
 * Check if a user can access a specific route
 * @param {Array} userPermissions - Array of permission keys the user has
 * @param {string} route - The route path
 * @returns {boolean}
 */
export const canAccessRoute = (userPermissions, route) => {
  const requiredPermissions = getPermissionsForRoute(route);
  
  if (requiredPermissions.length === 0) {
    return true; // No permissions required for this route
  }
  
  return hasAllPermissions(userPermissions, requiredPermissions);
};

/**
 * Filter menu items based on user permissions
 * @param {Array} menuItems - Array of menu items with permission requirements
 * @param {Array} userPermissions - Array of permission keys the user has
 * @returns {Array} Filtered menu items
 */
export const filterMenuItemsByPermissions = (menuItems, userPermissions) => {
  return menuItems.filter(item => {
    if (!item.permissions || item.permissions.length === 0) {
      return true; // No permissions required
    }
    
    if (item.permissions.some) {
      // If permissions is an array, check if user has any of them
      return hasAnyPermission(userPermissions, item.permissions);
    } else {
      // If permissions is a single permission key
      return hasPermission(userPermissions, item.permissions);
    }
  });
};

/**
 * Get all available permissions for the permission editor
 * @returns {Array} Array of permission objects grouped by category
 */
export const getAllPermissionsForEditor = () => {
  return Object.entries(PERMISSIONS_BY_CATEGORY).map(([category, permissions]) => ({
    category,
    permissions: permissions.map(p => ({
      key: p.key,
      label: p.label,
      description: p.description
    }))
  }));
};

/**
 * Auto-register a new feature with the permission system
 * This should be called when adding new features to ensure they're included in permissions
 * @param {string} permissionKey - The permission key
 * @param {Object} permissionData - The permission metadata
 */
export const registerPermission = (permissionKey, permissionData) => {
  if (PERMISSIONS[permissionKey]) {}

  PERMISSIONS[permissionKey] = {
    key: permissionKey,
    ...permissionData
  };

  // Update the category grouping
  const category = permissionData.category || 'Other';
  if (!PERMISSIONS_BY_CATEGORY[category]) {
    PERMISSIONS_BY_CATEGORY[category] = [];
  }
  PERMISSIONS_BY_CATEGORY[category].push(PERMISSIONS[permissionKey]);
};

// Export the permission keys for easy access
export const PERMISSION_KEYS = Object.keys(PERMISSIONS);






























